import { VariableFormat } from '@iotize/tap';
import { BodyConverter } from '@iotize/tap/client/api';
import {
  ArrayConverter,
  BooleanConverter,
  FloatConverter,
  NumberConverter,
  StringConverter,
} from '@iotize/tap/client/impl';
import { VariableConfig } from '@iotize/tap/config/schema/v1';
import { VariableType } from '@iotize/tap/service/impl/variable';

export const ASCII_WITH_END_OF_STRING_CHAR_CONVERTER = new StringConverter(
  'ascii',
  {
    addEndOfStringCharacter: true,
    replaceEmptyStringWithEndOfLineChar: true,
    stripEndOfStringCharacter: true,
  },
);

export function computeByteLengthFromFormat(info: {
  length: number;
  format: VariableFormat;
}): number {
  return Math.ceil(computeBitLengthFromFormat(info) / 8);
}

export function computeBitLengthFromFormat(info: {
  length: number;
  format: VariableFormat;
}): number {
  return info.length * variableFormatToBitSize(info.format);
}

export function variableFormatToByteSize(format: VariableFormat) {
  return Math.ceil(variableFormatToBitSize(format) / 8);
}

export function variableFormatToBitSize(format: VariableFormat) {
  switch (format) {
    case VariableFormat._1_BIT:
      return 1;
    case VariableFormat._8_BITS:
      return 8;
    case VariableFormat._16_BITS:
      return 16;
    case VariableFormat._32_BITS:
      return 32;
    default:
      // TODO better error
      throw new Error(
        `InternalError: missing bit length for format value value "${format}" (name: ${VariableFormat[format]})`,
      );
  }
}

export function variableDataTypeToByteSize(dataType: VariableType.Data) {
  return typeConfig[dataType].size;
}

/**
 * Map Variable Type to a byte size when encoded as a Tap frame.
 */
const typeConfig = {
  [VariableType.Data.BOOLEAN]: {
    size: 1,
  },
  [VariableType.Data.UINT8]: {
    size: 1,
  },
  [VariableType.Data.UINT16]: {
    size: 2,
  },
  [VariableType.Data.UINT32]: {
    size: 4,
  },
  [VariableType.Data.INT8]: {
    size: 1,
  },
  [VariableType.Data.INT16]: {
    size: 2,
  },
  [VariableType.Data.INT32]: {
    size: 4,
  },
  [VariableType.Data.FLOAT32]: {
    size: 4,
  },
  [VariableType.Data.ASCII]: {
    size: 1,
  },
};

export function getConverterFromVariableConfig(
  variableConfig: VariableConfig,
  stringConverter: BodyConverter<string> = ASCII_WITH_END_OF_STRING_CHAR_CONVERTER,
) {
  const typeKey =
    variableConfig.dataType as unknown as keyof typeof VariableType.Data;
  if (!(typeKey in VariableType.Data)) {
    throw new Error(`Unknown variable type key "${typeKey}"`); // TODO better error
  }
  return getConverterFromVariableType(
    VariableType.Data[typeKey],
    variableConfig.length || 1,
    stringConverter,
  );
}

/**
 * Get converter from variable type and length (number of element)
 * @param dataType
 * @param length > 1 if it's an array.
 *
 * @return undefined if converter does not exist for the given type
 */
export function getConverterFromVariableType(
  dataType: VariableType.Data,
  length = 1,
  stringConverter: BodyConverter<string> = ASCII_WITH_END_OF_STRING_CHAR_CONVERTER,
): BodyConverter<any> | undefined {
  if (dataType === VariableType.Data.ASCII) {
    return stringConverter;
  }
  const converter = getRawConverterFromVariableDataType(dataType);
  if (converter && length > 1) {
    return new ArrayConverter<any>(converter, {
      sizeOfItem: variableDataTypeToByteSize(dataType),
    });
  } else {
    return converter;
  }
}

// type TypeMapping = {
//     [VariableType.Data.ASCII]: string;
//     [VariableType.Data.FLOAT32]: number;
//     [VariableType.Data.INT32]: number;
//     [VariableType.Data.INT16]: number;
//     [VariableType.Data.INT8]: number;
//     [VariableType.Data.UINT8]: number;
//     [VariableType.Data.UINT16]: number;
//     [VariableType.Data.UINT32]: number;
// }

/**
 * Get raw converter instance from variable type
 * TODO improve output typing with mapping
 * @param format
 * @param length
 */
function getRawConverterFromVariableDataType(
  type: VariableType.Data,
): BodyConverter<boolean | number | string> | undefined {
  switch (type) {
    case VariableType.Data.BOOLEAN:
      return BooleanConverter.instanceBit0();
    case VariableType.Data.UINT8:
      return NumberConverter.uint8();
    case VariableType.Data.UINT16:
      return NumberConverter.uint16();
    case VariableType.Data.UINT32:
      return NumberConverter.uint32();
    case VariableType.Data.INT8:
      return NumberConverter.int8();
    case VariableType.Data.INT16:
      return NumberConverter.int16();
    case VariableType.Data.INT32:
      return NumberConverter.int32();
    case VariableType.Data.FLOAT32:
      return FloatConverter.instance32();
    case VariableType.Data.ASCII:
      return StringConverter.ascii();
    default:
      return undefined;
  }
}
